import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Header from "@/components/Header";

// Countdown until we auto-forward stale product / category / order deep
// links to the closest live index (e.g. an old /product/{id} URL after a
// catalogue reseed). For every OTHER unknown URL — arbitrary typos, third-
// party mis-links, expired campaigns — the visitor stays on the 404 page
// and picks a CTA. This keeps the promise from P0-7 ("never blank") while
// no longer silently rewriting typos to Home (which used to make the app
// look broken).
const REDIRECT_SECONDS = 5;

/**
 * Premium 404. Always renders content (never blank), announces to screen
 * readers, and auto-forwards ONLY stale deep-links (`/product/*`,
 * `/category/*`, `/orders*`) to the closest live index after
 * `REDIRECT_SECONDS`. Typo URLs keep the visitor on the 404 page so they
 * can spot the mistake and recover with one tap on either CTA.
 *
 * Also handles the Android App Links case: if a customer receives a deep
 * link to a stale product URL, the TWA lands here and forwards them to
 * `/shop`. Non-deep-link typos show the 404 page with `Back to home` and
 * `Browse the collection` buttons instead of silently changing the URL.
 */
const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [remaining, setRemaining] = useState(REDIRECT_SECONDS);

  // Decide whether this URL is a "recoverable deep-link" — a stale product,
  // category, or order URL where auto-forwarding to the closest live index
  // is friendlier than making the visitor tap. For every OTHER unknown URL
  // (typos like "/forgot", "/reset", "/password", third-party mis-links,
  // etc.) we deliberately DO NOT auto-redirect: a silent hop to "/" hid the
  // typo and made the app look broken. The visitor now stays on the 404
  // screen and keeps full control via the two CTAs below.
  const path = location.pathname || "";
  let redirectTarget = null;
  if (path.startsWith("/product/")) redirectTarget = "/shop";
  else if (path.startsWith("/category/")) redirectTarget = "/shop";
  else if (path.startsWith("/orders")) redirectTarget = "/profile";

  useEffect(() => {
    if (!redirectTarget) return; // typo URL — keep the visitor on the 404 page

    const tick = setInterval(() => {
      setRemaining((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    const jump = setTimeout(() => {
      navigate(redirectTarget, { replace: true });
    }, REDIRECT_SECONDS * 1000);
    return () => {
      clearInterval(tick);
      clearTimeout(jump);
    };
  }, [navigate, redirectTarget]);

  return (
    <div className="min-h-screen bg-white" data-testid="page-not-found">
      <Header showBack title="Page not found" />
      <div
        className="mx-auto max-w-md px-6 py-20 text-center"
        role="alert"
        aria-live="polite"
      >
        <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-[#B8912B]">
          Error 404
        </div>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl">
          We couldn&apos;t find that page
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          The page may have moved, been renamed, or never existed. Let&apos;s get
          you back on track.
        </p>
        {redirectTarget ? (
          <p
            className="mt-3 text-[11px] uppercase tracking-widest text-gray-400"
            data-testid="not-found-redirect-timer"
          >
            {remaining > 0
              ? `Taking you home in ${remaining} s…`
              : "Redirecting…"}
          </p>
        ) : null}
        <div className="mt-6 flex flex-col gap-2">
          <Link
            to="/"
            data-testid="not-found-home"
            className="bg-black py-3 text-[11px] font-semibold uppercase tracking-widest text-white"
          >
            Back to home
          </Link>
          <Link
            to="/shop"
            data-testid="not-found-browse"
            className="border border-black py-3 text-[11px] font-semibold uppercase tracking-widest text-black"
          >
            Browse the collection
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
